<template>
  <div class="px-3 py-4 u-position-relative" :class="{ 'data-point-is-concerning': dataPointIsConcerning }">
    <v-icon v-if="dataPointIsConcerning" class="data-point-warning" color="indicator-critical">
      mdi-alert-circle
    </v-icon>
    <div
      class="u-label-2 d-flex align-center justify-center"
      :class="dataPointIsConcerning ? 'indicator-critical--text' : ''"
      :style="{ color: getDataPointColor() }"
    >
      <div>{{ titleByLiveDashboardTypes[currentTab] }}</div>
    </div>
    <div
      class="u-display-5 text-align-center font-weight-bold num"
      :class="dataPointIsConcerning ? 'indicator-critical--text' : ''"
      :style="{ color: getDataPointColor(), opacity: opacity }"
    >
      <span>{{ measurementNumber }}</span>
      <small v-if="dataPoint != null" class="unit ml-1">{{ unitByLiveDashboardTypes[currentTab] }}</small>
    </div>
    <div class="flash" :class="{ remove: !enableFlash }"></div>
  </div>
</template>

<script>
import Vue from 'vue'
import * as R from 'ramda'
import moment from 'moment-timezone'
import { INCLUDE_ALL_METRICS_LIVE_AUDIO_ALERT } from '@/constants/app-variables'
import { LIVE_DASHBOARD_TYPES, titleByLiveDashboardTypes, unitByLiveDashboardTypes } from '@/constants/live-dashboard'
import { vitalSignIsConcerning } from '@/utils/live-data'
import round from 'lodash-es/round'
import { poundToKiloGram } from '@/utils/unit-converter'

export default Vue.extend({
  props: {
    currentTab: {
      type: String,
      required: true,
    },
    patientWithLiveDataLatest: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      watchForConcerningVitalSigns: INCLUDE_ALL_METRICS_LIVE_AUDIO_ALERT,
      titleByLiveDashboardTypes,
      unitByLiveDashboardTypes,
      colorByLiveDashboardTypes: {
        [LIVE_DASHBOARD_TYPES.OxygenSaturations]: this.$vuetify.theme.themes.light.teal,
        [LIVE_DASHBOARD_TYPES.PulseRates]: this.$vuetify.theme.themes.light.primary,
        [LIVE_DASHBOARD_TYPES.BodyTemperatures]: this.$vuetify.theme.themes.light.coral,
        [LIVE_DASHBOARD_TYPES.BodyWeights]: this.$vuetify.theme.themes.light.lavender,
        [LIVE_DASHBOARD_TYPES.BloodPressureSystolic]: this.$vuetify.theme.themes.light.burgundy,
        [LIVE_DASHBOARD_TYPES.BloodPressureDiastolic]: this.$vuetify.theme.themes.light.burgundy,
      },
      propPathByLiveDashboardTypes: {
        [LIVE_DASHBOARD_TYPES.OxygenSaturations]: ['patientWithLiveDataLatest', 'oxygenSaturation'],
        [LIVE_DASHBOARD_TYPES.PulseRates]: ['patientWithLiveDataLatest', 'pulseRate'],
        [LIVE_DASHBOARD_TYPES.BodyTemperatures]: ['patientWithLiveDataLatest', 'bodyTemperature'],
        [LIVE_DASHBOARD_TYPES.BodyWeights]: ['patientWithLiveDataLatest', 'bodyWeight'],
        [LIVE_DASHBOARD_TYPES.BloodPressureSystolic]: ['patientWithLiveDataLatest', 'bloodPressureSystolic'],
        [LIVE_DASHBOARD_TYPES.BloodPressureDiastolic]: ['patientWithLiveDataLatest', 'bloodPressureDiastolic'],
      },
      oldDataThresholdMin: 30,
      enableFlash: false,
    }
  },
  computed: {
    dataPoint() {
      const path = this.propPathByLiveDashboardTypes[this.currentTab] || []
      return R.pathOr(null, path, this)
    },
    opacity() {
      if (this.dataPoint == null) return 1

      const dataTime = moment(this.dataPoint.timestamp)
      const now = moment()
      const diffMin = now.diff(dataTime, 'minutes')
      return Math.max(0.35, 1 - diffMin / this.oldDataThresholdMin)
    },
    measurementNumber() {
      if (this.dataPoint == null) return '-'

      if (this.currentTab === LIVE_DASHBOARD_TYPES.BodyWeights) {
        return round(poundToKiloGram(this.dataPoint.value), 1)
      } else {
        return round(this.dataPoint.value, 1)
      }
    },
    dataPointIsConcerning() {
      if (this.watchForConcerningVitalSigns) {
        return vitalSignIsConcerning(this.currentTab, this.dataPoint)
      }
      return false
    },
  },
  watch: {
    dataPoint(n, o) {
      if (o.value === n.value) return
      if (o != null && o.value === n.value) return

      this.enableFlash = true
      window.setTimeout(() => {
        this.enableFlash = false
      }, 0)
    },
  },
  methods: {
    getDataPointColor() {
      return this.dataPointIsConcerning ? '' : this.colorByLiveDashboardTypes[this.currentTab]
    },
  },
})
</script>

<style lang="scss" scoped>
.num {
  line-height: 1;
}

.flash {
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: $color-yellow;
  opacity: 0.6;

  &.remove {
    transition: opacity 1.4s cubic-bezier(0.22, 1, 0.36, 1);
    opacity: 0;
  }
}

.unit {
  font-size: 18px;
  font-weight: normal;
}

.data-point-is-concerning {
  background-color: rgba($color-indicator-critical, 0.2);
}

.data-point-warning {
  position: absolute;
  top: 8px;
  left: 8px;
}
</style>
